/**
 * StudioPage — Conception de scénarios d'automatisation (US-151 E-7)
 * Route : /admin/studio
 * Accès : admin + cadre
 */

import { useEffect, useState } from 'react';
import { apiClient } from '@/shared/services/apiClient';

interface ScenarioStep {
  id: string;
  position: number;
  step_type: 'perception' | 'decision' | 'action';
  label: string | null;
  config: Record<string, unknown>;
}

interface Scenario {
  id: string;
  name: string;
  description: string | null;
  status: 'draft' | 'active' | 'archived';
  trigger_type: string | null;
  config: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  steps?: ScenarioStep[];
}

interface StepForm {
  position: number;
  step_type: 'perception' | 'decision' | 'action';
  label: string;
}

const STATUS_BADGE: Record<string, string> = {
  draft:    'bg-secondary',
  active:   'bg-success',
  archived: 'bg-dark',
};

const STEP_COLOR: Record<string, string> = {
  perception: '#0891b2',
  decision:   '#7c3aed',
  action:     '#d97706',
};

const TRIGGER_LABELS: Record<string, string> = {
  manual:   'Manuel',
  schedule: 'Planifié',
  event:    'Événement',
};

function fmt(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

const EMPTY_FORM = {
  name: '',
  description: '',
  trigger_type: 'manual' as string,
};

export function StudioPage() {
  const [scenarios, setScenarios]   = useState<Scenario[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);
  const [showModal, setShowModal]   = useState(false);
  const [saving, setSaving]         = useState(false);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [steps, setSteps]           = useState<StepForm[]>([]);
  const [formError, setFormError]   = useState<string | null>(null);
  const [selected, setSelected]     = useState<Scenario | null>(null);

  async function loadScenarios() {
    setLoading(true);
    setError(null);
    try {
      const data = await apiClient.get<Scenario[]>('/api/v1/internal/studio/scenarios');
      setScenarios(data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erreur chargement scénarios');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadScenarios(); }, []);

  function openCreate() {
    setForm(EMPTY_FORM);
    setSteps([]);
    setFormError(null);
    setShowModal(true);
  }

  function addStep() {
    setSteps(s => [...s, { position: s.length, step_type: 'action', label: '' }]);
  }

  function removeStep(idx: number) {
    setSteps(s => s.filter((_, i) => i !== idx).map((st, i) => ({ ...st, position: i })));
  }

  function updateStep(idx: number, patch: Partial<StepForm>) {
    setSteps(s => s.map((st, i) => i === idx ? { ...st, ...patch } : st));
  }

  async function handleCreate() {
    if (!form.name.trim()) {
      setFormError('Le nom est requis');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      await apiClient.post('/api/v1/internal/studio/scenarios', {
        name: form.name.trim(),
        description: form.description.trim() || null,
        trigger_type: form.trigger_type || null,
        steps: steps.map(s => ({
          position:  s.position,
          step_type: s.step_type,
          label:     s.label.trim() || null,
        })),
      });
      setShowModal(false);
      await loadScenarios();
    } catch (e: unknown) {
      setFormError(e instanceof Error ? e.message : 'Erreur création');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Désactiver ce scénario ?')) return;
    try {
      await apiClient.delete(`/api/v1/internal/studio/scenarios/${id}`);
      setSelected(null);
      await loadScenarios();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Erreur suppression');
    }
  }

  async function openDetail(scenario: Scenario) {
    try {
      const data = await apiClient.get<Scenario>(`/api/v1/internal/studio/scenarios/${scenario.id}`);
      setSelected(data);
    } catch {
      setSelected(scenario);
    }
  }

  return (
    <div className="container-fluid">
      <div className="row mb-4">
        <div className="col-12 d-flex justify-content-between align-items-start flex-wrap gap-2">
          <div>
            <h1 className="fw-bold mb-1" style={{ color: 'var(--gray-900)' }}>
              <i className="bi bi-diagram-2 me-2" style={{ color: 'var(--purple-primary)' }} />
              Studio
            </h1>
            <p className="text-muted mb-0">Conception de scénarios d'automatisation</p>
          </div>
          <button className="btn btn-primary btn-sm d-flex align-items-center gap-1" onClick={openCreate}>
            <i className="bi bi-plus-lg" />
            Nouveau scénario
          </button>
        </div>
      </div>

      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border" style={{ color: 'var(--purple-primary)' }} />
        </div>
      )}

      {error && <div className="alert alert-danger">{error}</div>}

      {!loading && !error && (
        <div className="row g-3">
          {scenarios.length === 0 && (
            <div className="col-12">
              <div className="alert alert-light border text-center py-4">
                <i className="bi bi-diagram-2 d-block mb-2" style={{ fontSize: '2rem', color: 'var(--purple-primary)' }} />
                <strong>Aucun scénario</strong>
                <br />
                <small className="text-muted">Créez votre premier scénario d'automatisation.</small>
              </div>
            </div>
          )}

          {scenarios.map(s => (
            <div key={s.id} className="col-md-6 col-xl-4">
              <div
                className="card h-100 shadow-sm"
                style={{ cursor: 'pointer', borderTop: `3px solid var(--purple-primary)` }}
                onClick={() => openDetail(s)}
              >
                <div className="card-body p-3">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <strong className="small lh-sm" style={{ maxWidth: '70%' }}>{s.name}</strong>
                    <span className={`badge ${STATUS_BADGE[s.status] ?? 'bg-secondary'}`}>{s.status}</span>
                  </div>
                  {s.description && (
                    <p className="small text-muted mb-2" style={{ lineHeight: 1.4 }}>{s.description}</p>
                  )}
                  <div className="d-flex justify-content-between align-items-center pt-2 border-top mt-auto">
                    <small className="text-muted">
                      <i className="bi bi-lightning me-1" />
                      {TRIGGER_LABELS[s.trigger_type ?? ''] ?? s.trigger_type ?? '—'}
                    </small>
                    <small className="text-muted">{fmt(s.updated_at)}</small>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Modal Création ───────────────────────────────────────────────── */}
      {showModal && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Nouveau scénario</h5>
                <button className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                {formError && <div className="alert alert-danger py-2 small">{formError}</div>}

                <div className="mb-3">
                  <label className="form-label fw-semibold small">Nom <span className="text-danger">*</span></label>
                  <input
                    className="form-control form-control-sm"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="Surveillance non-conformité médicament..."
                    maxLength={200}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold small">Description</label>
                  <textarea
                    className="form-control form-control-sm"
                    rows={2}
                    value={form.description}
                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="Objectif et contexte du scénario..."
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold small">Déclencheur</label>
                  <select
                    className="form-select form-select-sm"
                    value={form.trigger_type}
                    onChange={e => setForm(f => ({ ...f, trigger_type: e.target.value }))}
                  >
                    <option value="manual">Manuel</option>
                    <option value="schedule">Planifié</option>
                    <option value="event">Événement</option>
                  </select>
                </div>

                {/* Steps */}
                <div className="mb-2">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="form-label fw-semibold small mb-0">Étapes</label>
                    <button className="btn btn-outline-secondary btn-sm" onClick={addStep}>
                      <i className="bi bi-plus" /> Ajouter une étape
                    </button>
                  </div>

                  {steps.length === 0 && (
                    <p className="text-muted small fst-italic">Aucune étape. Ajoutez des blocs perception / décision / action.</p>
                  )}

                  {steps.map((step, idx) => (
                    <div key={idx} className="d-flex align-items-center gap-2 mb-2">
                      <span
                        className="badge"
                        style={{ background: STEP_COLOR[step.step_type], minWidth: '80px', fontSize: '0.7rem' }}
                      >
                        {idx + 1}
                      </span>
                      <select
                        className="form-select form-select-sm"
                        style={{ width: '140px' }}
                        value={step.step_type}
                        onChange={e => updateStep(idx, { step_type: e.target.value as StepForm['step_type'] })}
                      >
                        <option value="perception">Perception</option>
                        <option value="decision">Décision</option>
                        <option value="action">Action</option>
                      </select>
                      <input
                        className="form-control form-control-sm"
                        value={step.label}
                        onChange={e => updateStep(idx, { label: e.target.value })}
                        placeholder="Label de l'étape"
                      />
                      <button
                        className="btn btn-outline-danger btn-sm flex-shrink-0"
                        onClick={() => removeStep(idx)}
                      >
                        <i className="bi bi-trash" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary btn-sm" onClick={() => setShowModal(false)}>
                  Annuler
                </button>
                <button className="btn btn-primary btn-sm" onClick={handleCreate} disabled={saving}>
                  {saving ? <><span className="spinner-border spinner-border-sm me-1" />Création…</> : 'Créer le scénario'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Panneau détail ───────────────────────────────────────────────── */}
      {selected && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title mb-0">{selected.name}</h5>
                  <small className="text-muted font-monospace">{selected.id}</small>
                </div>
                <button className="btn-close" onClick={() => setSelected(null)} />
              </div>
              <div className="modal-body">
                <div className="row g-3 mb-3">
                  <div className="col-6">
                    <small className="text-muted text-uppercase fw-semibold d-block mb-1">Statut</small>
                    <span className={`badge ${STATUS_BADGE[selected.status]}`}>{selected.status}</span>
                  </div>
                  <div className="col-6">
                    <small className="text-muted text-uppercase fw-semibold d-block mb-1">Déclencheur</small>
                    <span>{TRIGGER_LABELS[selected.trigger_type ?? ''] ?? selected.trigger_type ?? '—'}</span>
                  </div>
                  {selected.description && (
                    <div className="col-12">
                      <small className="text-muted text-uppercase fw-semibold d-block mb-1">Description</small>
                      <p className="small mb-0">{selected.description}</p>
                    </div>
                  )}
                </div>

                {selected.steps && selected.steps.length > 0 && (
                  <div>
                    <small className="text-muted text-uppercase fw-semibold d-block mb-2">
                      Étapes ({selected.steps.length})
                    </small>
                    <div className="d-flex flex-column gap-2">
                      {selected.steps.map((step, i) => (
                        <div key={step.id} className="d-flex align-items-center gap-2 p-2 rounded"
                          style={{ background: 'var(--gray-50)', fontSize: '0.85rem' }}>
                          <span
                            className="badge"
                            style={{ background: STEP_COLOR[step.step_type], minWidth: '90px' }}
                          >
                            {step.step_type}
                          </span>
                          <span className="fw-semibold">{i + 1}.</span>
                          <span>{step.label ?? <em className="text-muted">Sans label</em>}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                {selected.status === 'draft' && (
                  <button
                    className="btn btn-outline-danger btn-sm"
                    onClick={() => handleDelete(selected.id)}
                  >
                    <i className="bi bi-trash me-1" />Désactiver
                  </button>
                )}
                <button className="btn btn-outline-secondary btn-sm" onClick={() => setSelected(null)}>
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
